/**
 * 1.6.1: Administration → Transcoding → the proxy bitrate cap ("Proxy bitrate cap (Mbit/s)", 1–20, admin; PUT
 * /api/v1/settings/proxy-bitrate-cap) and "Regenerate proxies above the cap…" (GET/POST /api/v1/proxy-regeneration,
 * …/stop): the count first, then a background job a few at a time, with its progress and Stop. Mounted in
 * TranscodingAdmin.tsx inside the proxy format card.
 */
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, RefreshCw, Square } from 'lucide-react';
import { PROXY_BITRATE_CAP_MAX_MBPS, PROXY_BITRATE_CAP_MIN_MBPS, PROXY_REGEN_CONCURRENCY, type ProxyRegenStatusDTO } from '@prism/contracts';
import { api, errorMessage } from '../../../api/client';
import { queryKeys } from '../../../api/queryKeys';
import { toast } from '../../../components/Toasts';

const regenKey = ['proxy-regeneration'] as const;
const proxies = (n: number) => (n === 1 ? '1 proxy' : `${n} proxies`);

export function ProxyCapCard({ capMbps, disabled }: { capMbps: number | undefined; disabled: boolean }) {
  const qc = useQueryClient();
  const [value, setValue] = useState<string>('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (capMbps != null) setValue(String(capMbps)); }, [capMbps]);
  const status = useQuery({
    queryKey: regenKey,
    queryFn: () => api<ProxyRegenStatusDTO>('/api/v1/proxy-regeneration'),
    refetchInterval: (q) => (q.state.data?.job?.status === 'running' ? 3000 : false),
  });
  const n = Number(value);
  const valid = Number.isInteger(n) && n >= PROXY_BITRATE_CAP_MIN_MBPS && n <= PROXY_BITRATE_CAP_MAX_MBPS;
  const dirty = capMbps != null && valid && n !== capMbps;
  const s = status.data;
  const job = s?.job;
  const running = job?.status === 'running';

  async function act(fn: () => Promise<unknown>, ok: string) {
    setBusy(true);
    try {
      await fn();
      await qc.invalidateQueries({ queryKey: queryKeys.transcodeProfiles() });
      await qc.invalidateQueries({ queryKey: regenKey });
      toast(ok);
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  }

  const startRegen = () => {
    if (!s?.aboveCap) return;
    if (!window.confirm(`Regenerate ${proxies(s.aboveCap)} above ${s.capMbps} Mbit/s with the current settings? They are made a few at a time, after other processing.`)) return;
    void act(() => api('/api/v1/proxy-regeneration', { method: 'POST', json: {} }), 'Regenerating proxies above the cap');
  };

  return (
    <div data-testid="proxy-cap" style={{ marginTop: 12 }}>
      <div className="field">
        <label htmlFor="proxy-bitrate-cap">Proxy bitrate cap (Mbit/s)</label>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input id="proxy-bitrate-cap" className="input" type="number" min={PROXY_BITRATE_CAP_MIN_MBPS} max={PROXY_BITRATE_CAP_MAX_MBPS} step={1} style={{ maxWidth: 96 }}
            value={value} disabled={disabled || busy} onChange={(e) => setValue(e.target.value)} data-testid="proxy-bitrate-cap" />
          <button className="btn sm" disabled={disabled || busy || !dirty}
            onClick={() => void act(() => api('/api/v1/settings/proxy-bitrate-cap', { method: 'PUT', json: { mbps: n } }), `Proxy bitrate cap: ${n} Mbit/s`)} data-testid="proxy-bitrate-cap-save">Save</button>
          {!valid && value !== '' && <span className="help" style={{ color: 'var(--danger)' }}>{PROXY_BITRATE_CAP_MIN_MBPS}–{PROXY_BITRATE_CAP_MAX_MBPS}</span>}
        </div>
        <span className="help">The most the built-in 720p video proxy may use, so scrubbing stays quick over the network. Software keeps its quality setting (CRF 23) below the cap; hardware encoders aim at three quarters of it. 4 by default. A proxy profile keeps its own options. Applies to proxies made from now on; existing proxies keep their bitrate until they are regenerated.</span>
      </div>

      <div className="field" data-testid="proxy-regen">
        <label>Existing proxies</label>
        {s && !running && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span data-testid="proxy-regen-count">{s.aboveCap === 0 ? `No proxy is above ${s.capMbps} Mbit/s` : `${proxies(s.aboveCap)} above ${s.capMbps} Mbit/s`}</span>
            <button className="btn sm" disabled={disabled || busy || !s.aboveCap} onClick={startRegen} data-testid="proxy-regen-start">
              {busy ? <Loader2 size={13} className="spin" /> : <RefreshCw size={13} />} Regenerate proxies above the cap…
            </button>
          </div>
        )}
        {job && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 6 }} data-testid="proxy-regen-job" data-status={job.status}>
            {running && <Loader2 size={13} className="spin" />}
            <span>
              {running ? 'Regenerating' : job.status === 'stopped' ? 'Stopped' : 'Finished'}: {job.succeeded} of {job.total} done
              {job.inFlight ? ` · ${job.inFlight} in progress` : ''}{job.failed ? ` · ${job.failed} failed` : ''}{job.skipped ? ` · ${job.skipped} skipped` : ''}
              <span className="faint"> · cap {job.capMbps} Mbit/s</span>
            </span>
            {running && (
              <button className="btn ghost sm danger" disabled={disabled || busy} onClick={() => void act(() => api('/api/v1/proxy-regeneration/stop', { method: 'POST', json: {} }), 'Stopped; proxies in progress finish')} data-testid="proxy-regen-stop">
                <Square size={12} /> Stop
              </button>
            )}
          </div>
        )}
        <span className="help">Finds video proxies above the cap (from their recorded bitrate, or file size ÷ duration) and makes them again with the current settings, {PROXY_REGEN_CONCURRENCY} at a time and only while other processing is light. Stop queues no more; those in progress finish.</span>
      </div>
    </div>
  );
}
